import { IconUserShield } from '@tabler/icons-react';
import {
  RadioCard,
  RadioGroup,
  RadioGroupProps,
} from '@zorro/design-system/v2/Radio';
import { EmployeeIcon } from '@zorro/shared/assets';
import {
  FormErrorMessage,
  Grid,
  Icon,
  Stack,
  Text,
} from '@zorro/web/design-system';
import { ReactNode } from 'react';
import {
  FieldPath,
  FieldValues,
  UseControllerProps,
  useController,
} from 'react-hook-form';

export enum AgentOrEmployee {
  AGENT = 'AGENT',
  EMPLOYEE = 'EMPLOYEE',
}

type Props<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = UseControllerProps<TFieldValues, TName> & {
  radioGroupProps?: RadioGroupProps;
  label?: string;
  description?: ReactNode;
  isDisabled?: boolean;
  onSendEvent?: (value: string) => Promise<void> | void;
};

export function AgentOrEmployeeInput<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  radioGroupProps,
  label,
  description,
  isDisabled,
  onSendEvent,
  ...formProps
}: Props<TFieldValues, TName>) {
  const {
    field,
    formState: { errors },
  } = useController(formProps);

  return (
    <>
      <Stack gap="md">
        {label && (
          <Text size="sm" fw={600}>
            {label}
          </Text>
        )}
        <Stack gap="xs">
          {description}
          <RadioGroup
            {...field}
            onChange={async (value) => {
              field.onChange(value);
              await onSendEvent?.(value);
            }}
          >
            <Grid>
              <Grid.Col span={6}>
                <RadioCard
                  icon={<Icon icon={IconUserShield} />}
                  iconColor="indigo"
                  label="Enrollment team agent"
                  value={AgentOrEmployee.AGENT}
                  disabled={isDisabled}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <RadioCard
                  icon={<Icon icon={EmployeeIcon} />}
                  iconColor="indigo"
                  label="Employee"
                  value={AgentOrEmployee.EMPLOYEE}
                  disabled={isDisabled}
                />
              </Grid.Col>
            </Grid>
          </RadioGroup>
        </Stack>
      </Stack>
      <FormErrorMessage fieldName={formProps.name} errors={errors} />
    </>
  );
}

AgentOrEmployeeInput.displayName = 'AgentOrEmployeeInput';
